import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

/** Mirrors FOOTER_DEFAULTS on the server, for clients saved before it existed. */
const SECTION_KEYS = ["about", "visit", "contact", "delivery", "policies", "custom", "bottom"];
export const EMPTY_FOOTER = {
  show: Object.fromEntries(SECTION_KEYS.map((k) => [k, true])),
  story: "",
  address: "",
  map_url: "",
  hours: [],
  phone: "",
  email: "",
  payments: [],
  policies: [],
  sections: [],
  copyright: "",
};

const PAYMENT_IDEAS = ["Cash", "Bank transfer", "Card", "Lynk", "PayPal", "Cash on delivery"];
const POLICY_IDEAS = [
  "Returns and refunds",
  "Delivery policy",
  "Privacy policy",
  "Terms and conditions",
  "Frequently asked questions",
];
const HOURS_STARTER = [
  { label: "Mon – Fri", time: "" },
  { label: "Saturday", time: "" },
  { label: "Sunday", time: "Closed" },
];

/** One footer section: a title, an on/off switch, and its fields. */
function FooterCard({ title, hint, on, onToggle, children }) {
  return (
    <div className="panel panel-pad">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="eyebrow">{title}</div>
          {hint && <p className="text-[12.5px] text-muted2 mt-1.5 max-w-lg">{hint}</p>}
        </div>
        <label className="flex items-center gap-2 cursor-pointer shrink-0">
          <span className="text-[12px] text-muted2">{on ? "Showing" : "Hidden"}</span>
          <Switch checked={on} onCheckedChange={onToggle} aria-label={`Show ${title}`} />
        </label>
      </div>
      <div className={cn("mt-5 space-y-4", !on && "opacity-50")}>{children}</div>
    </div>
  );
}

function RemoveButton({ onClick, label = "Remove" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="p-1.5 text-muted2 hover:text-stock-out rounded-lg hover:bg-rose-50 shrink-0"
    >
      <Trash2 className="w-4 h-4" />
    </button>
  );
}

/** A list of title + text entries — policies, or their own sections. */
function EntryList({ rows, max, onChange, titleHint, bodyHint, bodyRows, ideas, addLabel }) {
  const update = (i, field, value) =>
    onChange(rows.map((r, n) => (n === i ? { ...r, [field]: value } : r)));
  const unused = (ideas || []).filter((t) => !rows.some((r) => r.title === t));
  return (
    <div className="space-y-3">
      {rows.map((r, i) => (
        <div key={i} className="rounded-xl border border-line p-3 space-y-2">
          <div className="flex items-center gap-2">
            <Input
              value={r.title}
              onChange={(e) => update(i, "title", e.target.value)}
              placeholder={titleHint}
            />
            <RemoveButton onClick={() => onChange(rows.filter((_, n) => n !== i))} />
          </div>
          <Textarea
            rows={bodyRows}
            value={r.body}
            onChange={(e) => update(i, "body", e.target.value)}
            placeholder={bodyHint}
          />
        </div>
      ))}
      {rows.length < max && (
        <div className="flex flex-wrap gap-2">
          {unused.slice(0, 4).map((t) => (
            <Button
              key={t}
              type="button"
              size="sm"
              variant="outline"
              onClick={() => onChange([...rows, { title: t, body: "" }])}
            >
              <Plus className="w-3.5 h-3.5 mr-1.5" /> {t}
            </Button>
          ))}
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => onChange([...rows, { title: "", body: "" }])}
          >
            <Plus className="w-3.5 h-3.5 mr-1.5" /> {addLabel}
          </Button>
        </div>
      )}
      <p className="text-[12px] text-muted2">Anything missing a title or text isn't saved.</p>
    </div>
  );
}

/**
 * Settings → Footer: everything at the bottom of the shop in one place. Each
 * section has its own switch, so it can be hidden without losing what's in it.
 */
export default function FooterSettings({ shop, onChange, businessName, shopEnabled }) {
  const footer = { ...EMPTY_FOOTER, ...(shop?.footer || {}) };
  const show = { ...EMPTY_FOOTER.show, ...(footer.show || {}) };
  const [payment, setPayment] = useState("");

  const setShop = (patch) => onChange({ ...shop, ...patch });
  const setFooter = (patch) => setShop({ footer: { ...footer, ...patch } });
  const toggle = (key) => (v) => setFooter({ show: { ...show, [key]: v } });

  const addPayment = (name) => {
    const p = name.trim();
    if (!p || footer.payments.some((x) => x.toLowerCase() === p.toLowerCase())) return;
    setFooter({ payments: [...footer.payments, p] });
    setPayment("");
  };

  const year = new Date().getFullYear();

  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-line bg-white px-4 py-3 text-[13px] text-muted2 max-w-3xl">
        Everything here shows at the bottom of your shop. Switch a section off to hide it — what
        you've written is kept. A section with nothing filled in stays hidden on its own.
        {!shopEnabled && (
          <span className="block mt-1 text-ink">
            Your shop is off right now, so customers won't see any of this until you turn it on in
            the Shop tab.
          </span>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-5 items-start">
        <FooterCard
          title="About us"
          hint="A short line, plus a longer story behind a “Read our story” link."
          on={show.about}
          onToggle={toggle("about")}
        >
          <div className="space-y-1.5">
            <Label htmlFor="ft-about">Short about</Label>
            <Textarea
              id="ft-about"
              rows={2}
              value={shop?.about || ""}
              onChange={(e) => setShop({ about: e.target.value })}
              placeholder="Hand-packed gift boxes from Montego Bay, made to order."
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ft-story">
              Our story <span className="font-normal text-muted2">(optional)</span>
            </Label>
            <Textarea
              id="ft-story"
              rows={5}
              value={footer.story}
              onChange={(e) => setFooter({ story: e.target.value })}
              placeholder="How it started, who's behind it, what makes it special."
            />
          </div>
        </FooterCard>

        <FooterCard
          title="Visit us"
          hint="Where to find you and when you're open."
          on={show.visit}
          onToggle={toggle("visit")}
        >
          <div className="space-y-1.5">
            <Label htmlFor="ft-addr">Address</Label>
            <Textarea
              id="ft-addr"
              rows={2}
              value={footer.address}
              onChange={(e) => setFooter({ address: e.target.value })}
              placeholder="Shop 4, Fairview Plaza, Montego Bay"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ft-map">
              Map link <span className="font-normal text-muted2">(optional)</span>
            </Label>
            <Input
              id="ft-map"
              value={footer.map_url}
              onChange={(e) => setFooter({ map_url: e.target.value })}
              placeholder="Paste a Google Maps link"
            />
            <p className="text-[12px] text-muted2">
              Without one, “Get directions” searches the address.
            </p>
          </div>
          <div className="space-y-2">
            <Label className="block">Opening hours</Label>
            {footer.hours.map((h, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input
                  className="w-[140px] shrink-0"
                  value={h.label}
                  onChange={(e) =>
                    setFooter({
                      hours: footer.hours.map((r, n) => (n === i ? { ...r, label: e.target.value } : r)),
                    })
                  }
                  placeholder="Mon – Fri"
                />
                <Input
                  value={h.time}
                  onChange={(e) =>
                    setFooter({
                      hours: footer.hours.map((r, n) => (n === i ? { ...r, time: e.target.value } : r)),
                    })
                  }
                  placeholder="9am – 6pm"
                />
                <RemoveButton
                  onClick={() => setFooter({ hours: footer.hours.filter((_, n) => n !== i) })}
                />
              </div>
            ))}
            <div className="flex flex-wrap gap-2">
              {footer.hours.length === 0 && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setFooter({ hours: HOURS_STARTER })}
                >
                  <Plus className="w-3.5 h-3.5 mr-1.5" /> Start with weekdays and weekend
                </Button>
              )}
              {footer.hours.length < 10 && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setFooter({ hours: [...footer.hours, { label: "", time: "" }] })}
                >
                  <Plus className="w-3.5 h-3.5 mr-1.5" /> Add a row
                </Button>
              )}
            </div>
          </div>
        </FooterCard>

        <FooterCard
          title="Contact"
          hint="How customers reach you, and where else to find you."
          on={show.contact}
          onToggle={toggle("contact")}
        >
          <label className="flex items-center gap-3 cursor-pointer">
            <Switch
              checked={shop?.show_number !== false}
              onCheckedChange={(v) => setShop({ show_number: v })}
            />
            <span className="text-[13px] text-ink">
              Show the WhatsApp number{shop?.whatsapp ? ` (${shop.whatsapp})` : ""}
            </span>
          </label>
          <p className="text-[12px] text-muted2 -mt-2">
            The number itself is set in the Shop tab, since orders go to it.
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="ft-phone">Phone</Label>
              <Input
                id="ft-phone"
                value={footer.phone}
                onChange={(e) => setFooter({ phone: e.target.value })}
                placeholder="876-555-0100"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ft-email">Email</Label>
              <Input
                id="ft-email"
                type="email"
                value={footer.email}
                onChange={(e) => setFooter({ email: e.target.value })}
                placeholder="hello@yourbusiness.com"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="block">Social media</Label>
            {[
              { key: "instagram", label: "Instagram", hint: "@yourbusiness" },
              { key: "tiktok", label: "TikTok", hint: "@yourbusiness" },
              { key: "facebook", label: "Facebook", hint: "facebook.com/yourbusiness" },
              { key: "youtube", label: "YouTube", hint: "@yourchannel" },
            ].map((o) => (
              <div key={o.key} className="flex items-center gap-2.5">
                <span className="text-[12.5px] text-muted2 w-[72px] shrink-0">{o.label}</span>
                <Input
                  value={shop?.[o.key] || ""}
                  onChange={(e) => setShop({ [o.key]: e.target.value })}
                  placeholder={o.hint}
                />
              </div>
            ))}
          </div>
          <div className="space-y-2">
            <Label className="block">Other links</Label>
            {(shop?.links || []).map((l, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input
                  className="w-[120px] shrink-0"
                  value={l.label || ""}
                  onChange={(e) =>
                    setShop({
                      links: shop.links.map((x, n) => (n === i ? { ...x, label: e.target.value } : x)),
                    })
                  }
                  placeholder="What to call it"
                />
                <Input
                  value={l.url || ""}
                  onChange={(e) =>
                    setShop({
                      links: shop.links.map((x, n) => (n === i ? { ...x, url: e.target.value } : x)),
                    })
                  }
                  placeholder="Where it goes"
                />
                <RemoveButton
                  onClick={() => setShop({ links: shop.links.filter((_, n) => n !== i) })}
                />
              </div>
            ))}
            {(shop?.links || []).length < 8 && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setShop({ links: [...(shop?.links || []), { label: "", url: "" }] })}
              >
                <Plus className="w-3.5 h-3.5 mr-1.5" /> Add a link
              </Button>
            )}
          </div>
        </FooterCard>

        <FooterCard
          title="Delivery and payment"
          hint="How orders reach customers, and how they can pay."
          on={show.delivery}
          onToggle={toggle("delivery")}
        >
          <div className="space-y-1.5">
            <Label htmlFor="ft-del">Delivery</Label>
            <Textarea
              id="ft-del"
              rows={2}
              value={shop?.delivery || ""}
              onChange={(e) => setShop({ delivery: e.target.value })}
              placeholder="Same day in Kingston. Islandwide in 2–3 days."
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ft-pay">Ways to pay</Label>
            {footer.payments.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {footer.payments.map((p) => (
                  <span
                    key={p}
                    className="inline-flex items-center gap-1 rounded-full bg-brand-light text-brand pl-3 pr-1 py-0.5 text-[12.5px]"
                  >
                    {p}
                    <button
                      type="button"
                      onClick={() => setFooter({ payments: footer.payments.filter((x) => x !== p) })}
                      aria-label={`Remove ${p}`}
                      className="p-1 rounded-full hover:bg-white/70"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <Input
                id="ft-pay"
                value={payment}
                onChange={(e) => setPayment(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addPayment(payment);
                  }
                }}
                placeholder="Type one and press Enter"
              />
              <Button type="button" variant="outline" onClick={() => addPayment(payment)}>
                Add
              </Button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {PAYMENT_IDEAS.filter(
                (p) => !footer.payments.some((x) => x.toLowerCase() === p.toLowerCase())
              ).map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => addPayment(p)}
                  className="rounded-full border border-dashed border-line px-2.5 py-1 text-[12px] text-muted2 hover:border-brand hover:text-brand"
                >
                  + {p}
                </button>
              ))}
            </div>
          </div>
        </FooterCard>

        <FooterCard
          title="Policies"
          hint="Returns, privacy, FAQs and the like. Each shows as a link that opens its full text."
          on={show.policies}
          onToggle={toggle("policies")}
        >
          <EntryList
            rows={footer.policies}
            max={8}
            onChange={(policies) => setFooter({ policies })}
            titleHint="Returns and refunds"
            bodyHint="What can be returned, how long customers have, and how refunds are paid."
            bodyRows={4}
            ideas={POLICY_IDEAS}
            addLabel="Another policy"
          />
        </FooterCard>

        <FooterCard
          title="Your own sections"
          hint="Anything else — a note about custom orders, events, a guarantee. Each is its own column."
          on={show.custom}
          onToggle={toggle("custom")}
        >
          <EntryList
            rows={footer.sections}
            max={4}
            onChange={(sections) => setFooter({ sections })}
            titleHint="Custom orders"
            bodyHint="A few lines customers should know."
            bodyRows={3}
            addLabel="Add a section"
          />
        </FooterCard>

        <FooterCard
          title="Bottom line"
          hint="The small print along the very bottom."
          on={show.bottom}
          onToggle={toggle("bottom")}
        >
          <div className="space-y-1.5">
            <Label htmlFor="ft-copy">Copyright line</Label>
            <Input
              id="ft-copy"
              value={footer.copyright}
              onChange={(e) => setFooter({ copyright: e.target.value })}
              placeholder={`© ${year} ${businessName || "Your business"}`}
            />
            <p className="text-[12px] text-muted2">
              Leave it empty to use “© {year} {businessName || "your business name"}”, which
              updates each year.
            </p>
          </div>
        </FooterCard>
      </div>
    </div>
  );
}
